import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { PLATFORM_FEATURES } from "../../data/landing";
import { SectionEyebrow, fadeUp, stagger } from "./Primitives";

export default function Features() {
  const [expanded, setExpanded] = useState(false);
  const sectionRef = useRef(null);

  // Responsive state logic to determine initial counts
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getInitialCount = () => {
    if (windowWidth >= 1024) return 4;
    if (windowWidth >= 768) return 2;
    return 1;
  };

  const initialCount = getInitialCount();
  const displayedTools = expanded ? PLATFORM_FEATURES : PLATFORM_FEATURES.slice(0, initialCount);

  const handleToggle = () => {
    if (expanded) {
      setExpanded(false);
      // Smoothly scroll back to the beginning of the section
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      setExpanded(true);
    }
  };

  return (
    <section ref={sectionRef} id="features" className="relative py-20 lg:py-28">
      <div className="max-w-[1600px] w-full mx-auto px-4 sm:px-6 md:px-10 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl lg:max-w-5xl"
        >
          <SectionEyebrow>Platform</SectionEyebrow>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900">
            Every tool a modern startup actually needs.
          </h2>
          <p className="mt-4 text-base md:text-lg text-gray-600 leading-relaxed">
            Twelve specialised modules, one beautifully connected workspace — strategy, brand, marketing,
            funding and analytics in a single canvas.
          </p>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
        >
          {displayedTools.map((f, index) => {
            const Icon = Icons[f.icon] || Icons.Sparkles;
            const isRevealed = index >= initialCount;
            return (
              <motion.div
                key={f.title}
                variants={isRevealed ? undefined : fadeUp}
                initial={isRevealed ? { opacity: 0, y: 24 } : undefined}
                animate={isRevealed ? { opacity: 1, y: 0 } : undefined}
                transition={isRevealed ? { duration: 0.3, ease: [0.25, 1, 0.5, 1] } : undefined}
                whileHover={{ y: -6 }}
                className="group relative glass rounded-3xl p-6 shimmer-border transition-all duration-300 hover:shadow-[0_18px_50px_rgba(0,0,0,0.08)] border border-gray-100/70"
                data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div
                  className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${f.gradient} flex items-center justify-center text-white shadow-lg`}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold text-gray-900">{f.title}</h3>
                <p className="mt-2 text-sm text-gray-600 leading-relaxed">{f.desc}</p>
                <button
                  className="mt-5 inline-flex items-center gap-1 text-xs font-semibold text-gray-700 group-hover:text-[#7C3AED] transition-colors duration-300"
                  data-testid={`feature-learn-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  Learn more
                  <Icons.ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
              </motion.div>
            );
          })}
        </motion.div>

        {PLATFORM_FEATURES.length > initialCount && (
          <div className="mt-12 flex justify-center">
            <button
              onClick={handleToggle}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white shadow-md hover:shadow-lg hover:scale-[1.02] transition-all duration-300 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-[#7C3AED]/20"
              data-testid="toggle-features-btn"
            >
              {expanded ? "Show Less" : "View More"}
              {expanded ? (
                <Icons.ChevronUp className="w-4 h-4 text-white" />
              ) : (
                <Icons.ChevronDown className="w-4 h-4 text-white" />
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
